'use client'

import React, { useState } from 'react'
import type { CampAnmeldung, Teilnehmer, Camp } from '@/payload-types'
import AnmeldungenViewWrapper from './AnmeldungenViewWrapper.client'
import { BulkStatusManagementDialog } from './BulkStatusManagementDialog'
import { useRouter } from 'next/navigation'

type PopulatedAnmeldung = CampAnmeldung & {
  teilnehmer: Teilnehmer
  camp: Camp
  alternativeCamp?: Camp | null
}

interface AnmeldungenViewWrapperWithSelectionProps {
  anmeldungen: PopulatedAnmeldung[]
  alternativeRegistrations?: PopulatedAnmeldung[]
  groupedAnmeldungen: Record<string, { camp: Camp; anmeldungen: PopulatedAnmeldung[] }>
  selectedCampId?: string
  searchTerm?: string
  emailSearch?: string
}

const AnmeldungenViewWrapperWithSelection: React.FC<AnmeldungenViewWrapperWithSelectionProps> = ({
  anmeldungen,
  alternativeRegistrations = [],
  groupedAnmeldungen,
  selectedCampId,
  searchTerm,
  emailSearch,
}) => {
  const [selectedAnmeldungen, setSelectedAnmeldungen] = useState<Set<string>>(new Set())
  const [isBulkDialogOpen, setIsBulkDialogOpen] = useState(false)
  const router = useRouter()

  const handleSelectAnmeldung = (anmeldungId: string) => {
    const newSelection = new Set(selectedAnmeldungen)
    if (newSelection.has(anmeldungId)) {
      newSelection.delete(anmeldungId)
    } else {
      newSelection.add(anmeldungId)
    }
    setSelectedAnmeldungen(newSelection)
  }

  const handleSelectAll = () => {
    if (selectedAnmeldungen.size === anmeldungen.length) {
      setSelectedAnmeldungen(new Set())
    } else {
      setSelectedAnmeldungen(new Set(anmeldungen.map((a) => a.id)))
    }
  }

  const handleClearSelection = () => {
    setSelectedAnmeldungen(new Set())
  }

  const handleBulkStatusUpdate = () => {
    setIsBulkDialogOpen(true)
  }

  const handleBulkStatusUpdateSubmit = async (anmeldungIds: string[], newStatus: string) => {
    try {
      const response = await fetch(`/api/admin/camp-anmeldung/bulk-update`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          anmeldungIds,
          status: newStatus,
        }),
      })

      const result = await response.json()

      if (result.success) {
        // Use Payload's toast system
        window.dispatchEvent(
          new CustomEvent('payload-toast', {
            detail: {
              type: 'success',
              message: `${anmeldungIds.length} Anmeldungen erfolgreich aktualisiert!`,
            },
          }),
        )

        // Clear selection and refresh
        setSelectedAnmeldungen(new Set())
        router.refresh()
      } else {
        window.dispatchEvent(
          new CustomEvent('payload-toast', {
            detail: {
              type: 'error',
              message: result.error || 'Fehler beim Aktualisieren der Status',
            },
          }),
        )
      }
    } catch (error) {
      console.error('Error updating bulk status:', error)
      window.dispatchEvent(
        new CustomEvent('payload-toast', {
          detail: {
            type: 'error',
            message: 'Fehler beim Aktualisieren der Status',
          },
        }),
      )
    }
  }

  return (
    <>
      <AnmeldungenViewWrapper
        anmeldungen={anmeldungen}
        alternativeRegistrations={alternativeRegistrations}
        groupedAnmeldungen={groupedAnmeldungen}
        selectedCampId={selectedCampId}
        searchTerm={searchTerm}
        emailSearch={emailSearch}
        selectedCount={selectedAnmeldungen.size}
        onBulkStatusUpdate={handleBulkStatusUpdate}
        onClearSelection={handleClearSelection}
        selectedAnmeldungen={selectedAnmeldungen}
        onSelectAnmeldung={handleSelectAnmeldung}
        onSelectAll={handleSelectAll}
      />

      <BulkStatusManagementDialog
        isOpen={isBulkDialogOpen}
        onClose={() => setIsBulkDialogOpen(false)}
        selectedAnmeldungen={Array.from(selectedAnmeldungen)}
        anmeldungen={anmeldungen}
        onBulkStatusUpdate={handleBulkStatusUpdateSubmit}
      />
    </>
  )
}

export default AnmeldungenViewWrapperWithSelection
